import React, { createContext, useContext, useEffect, useState } from 'react';
import chatService from '../services/chatService';
import socketService from '../services/socketService';

const ChatContext = createContext();

export const useChat = () => useContext(ChatContext);

function getLastSeen(roomId) {
  return parseInt(localStorage.getItem(`chat_last_seen_${roomId}`) || '0');
}
function setLastSeen(roomId, ts) {
  localStorage.setItem(`chat_last_seen_${roomId}`, ts.toString());
}

export function ChatProvider({ children, currentUser }) {
  const [rooms, setRooms] = useState([]); // array of rooms with unreadCount
  const [activeRoomId, setActiveRoomId] = useState(null);

  // initial fetch rooms
  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      const fetched = await chatService.listRooms();
      const deduped = {};
      fetched.forEach(r=>{
        const other = r.participants.find(p=>p._id.toString()!==currentUser._id.toString());
        if(!other) return;
        const key = other._id;
        if(!deduped[key] || new Date(r.updatedAt)>new Date(deduped[key].updatedAt)) deduped[key]=r;
      });
      const roomsWithUnread = Object.values(deduped)
        .filter(r=>r.lastMessage && r.lastMessage.trim()!=='')
        .map(r=>({
          ...r,
          unread: r.updatedAt && new Date(r.updatedAt).getTime() > getLastSeen(r._id)?1:0
        }));
      setRooms(roomsWithUnread);
    })();
    socketService.onMessage(handleIncoming);
    return () => socketService.offMessage(handleIncoming);
  }, [currentUser]);

  const refreshRooms = async () => {
    const fetched = await chatService.listRooms();
    const deduped = {};
    fetched.forEach(r=>{
      const other = r.participants.find(p=>p._id.toString()!==currentUser._id.toString());
      if(!other) return;
      const key=other._id;
      if(!deduped[key]|| new Date(r.updatedAt)>new Date(deduped[key].updatedAt)) deduped[key]=r;
    });
    const list = Object.values(deduped)
      .filter(r=>r.lastMessage && r.lastMessage.trim()!=='')
      .map(r=>({
        ...r,
        unread: r.updatedAt && new Date(r.updatedAt).getTime() > getLastSeen(r._id)?1:0
      }));
    setRooms(list);
  };

  const handleIncoming = (msg) => {
    setRooms(prev => {
      const idx = prev.findIndex(r => r._id === msg.roomId);
      if (idx === -1) {
        // Unknown room (new conversation) → refresh list
        refreshRooms();
        return prev;
      }
      const newRooms = [...prev];
      const r = { ...newRooms[idx] };
      r.lastMessage = msg.text;
      r.updatedAt = msg.createdAt;
      if (msg.senderId._id !== currentUser._id && msg.roomId !== activeRoomId) {
        r.unread = (r.unread || 0) + 1;
      }
      newRooms[idx] = r;
      return newRooms;
    });
  };

  const addOrUpdateRoom = (room)=>{
    if(!room.lastMessage || room.lastMessage.trim()==='') return;
    setRooms(prev=>{
      const other = room.participants.find(p=>p._id.toString()!==currentUser._id.toString());
      if(!other) return prev;
      const key = other._id;
      const existsIdx = prev.findIndex(r=> r.participants.find(p=>p._id===key));
      const newRoom={...room, unread:0};
      if(existsIdx===-1) return [newRoom,...prev];
      const list=[...prev];
      list[existsIdx]=newRoom;
      return list;
    });
  };

  const openRoom = (room) => {
    setActiveRoomId(room._id);
    // reset unread
    setRooms(prev => prev.map(r => (r._id === room._id ? { ...r, unread: 0 } : r)));
    setLastSeen(room._id, Date.now());
    // Inform server to persist last-seen to suppress unread emails
    fetch(`/api/chats/${room._id}/seen`, { method: 'POST', credentials: 'include' }).catch(()=>{});
  };

  const value = { rooms, activeRoomId, openRoom, currentUser, addOrUpdateRoom };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
} 